// FormAuswahl.jsx — Form (Kata, Poomsae, Tul, Hyong) aus dem Katalog wählen (21.10.2026)
//
// Entscheidung des Veranstalters: kein Freitext. Erst Stil, dann Form; das
// Suchfeld filtert nur die Liste, gespeichert wird allein die Katalog-Nummer.
// Die Sammel-Einträge (Stil „Allgemein“: Freie Form, Musikform …) stehen unter
// jedem Stil am Ende, damit niemand dafür den Stil wechseln muss.
import React, { useEffect, useMemo, useState } from 'react';
import './FormAuswahl.css';

const API_URL = process.env.REACT_APP_API_URL || '';
export const SAMMEL_STIL = 'Allgemein';
const ALLE = '';

let katalogSpeicher = null;     // einmal je Seite laden
let katalogLaden = null;

/** Katalog vom Server (öffentlich). Wirft bei Fehler, damit er sichtbar wird. */
export function ladeFormenKatalog() {
  if (katalogSpeicher) return Promise.resolve(katalogSpeicher);
  if (!katalogLaden) {
    katalogLaden = fetch(`${API_URL}/api/formen/katalog`)
      .then(async (res) => {
        const d = await res.json().catch(() => ({}));
        if (!res.ok || !d.success) throw new Error(d.error || `Formenkatalog: Fehler ${res.status}`);
        katalogSpeicher = d.data || [];
        return katalogSpeicher;
      })
      .finally(() => { katalogLaden = null; });
  }
  return katalogLaden;
}

export function useFormenKatalog() {
  const [katalog, setKatalog] = useState(katalogSpeicher || []);
  const [fehler, setFehler] = useState(null);
  useEffect(() => {
    let aktiv = true;
    ladeFormenKatalog()
      .then((k) => { if (aktiv) { setKatalog(k); setFehler(null); } })
      .catch((e) => { if (aktiv) setFehler(e.message); });
    return () => { aktiv = false; };
  }, []);
  return { katalog, fehler };
}

// Zuletzt gewählter Stil: je Kategorie, sonst je Turnier (nur Bequemlichkeit,
// darf fehlen — privates Fenster, gesperrter Speicher).
const speicherSchluessel = (turnierId, kategorieId) => [
  kategorieId ? `formStil:${turnierId}:${kategorieId}` : null,
  `formStil:${turnierId}`,
].filter(Boolean);

export function merkeStil(turnierId, kategorieId, stil) {
  if (!stil || stil === SAMMEL_STIL) return;
  for (const k of speicherSchluessel(turnierId, kategorieId)) {
    try { localStorage.setItem(k, stil); } catch { /* ohne Speicher geht es auch */ }
  }
}

/** Vorbelegung: gemerkter Stil (Kategorie, dann Turnier), sonst der in der
 *  Kategorie häufigste schon gewählte Stil, sonst keiner. */
export function vorbelegterStil(turnierId, kategorieId, teilnehmer) {
  for (const k of speicherSchluessel(turnierId, kategorieId)) {
    try { const s = localStorage.getItem(k); if (s) return s; } catch { /* s. o. */ }
  }
  const zaehler = {};
  for (const t of teilnehmer || []) {
    if (t.form_stil && t.form_stil !== SAMMEL_STIL) zaehler[t.form_stil] = (zaehler[t.form_stil] || 0) + 1;
  }
  const best = Object.entries(zaehler).sort((a, b) => b[1] - a[1])[0];
  return best ? best[0] : ALLE;
}

// Suche ohne Gross/Klein, Akzente, Bindestriche und Leerzeichen:
// „heiangodan“ findet „Heian Godan“, „taeguk“ findet „Taegeuk“ nicht —
// dafür stehen die alten Schreibweisen in alt_namen.
const glatt = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '')
  .toLowerCase().replace(/[\s\-_'’.()]/g, '');

export default function FormAuswahl({ katalog, wert, startStil, onWaehle, beschaeftigt, kompakt }) {
  const stile = useMemo(() => {
    const s = [];
    for (const e of katalog) if (e.stil !== SAMMEL_STIL && !s.includes(e.stil)) s.push(e.stil);
    return s.sort((a, b) => a.localeCompare(b, 'de'));
  }, [katalog]);
  const gewaehlt = katalog.find((e) => e.id === wert) || null;
  const [stil, setStil] = useState(() => (gewaehlt && gewaehlt.stil !== SAMMEL_STIL ? gewaehlt.stil : (startStil || ALLE)));
  const [suche, setSuche] = useState('');

  // Vorbelegung kann nach dem Laden des Katalogs kommen.
  useEffect(() => {
    if (gewaehlt && gewaehlt.stil !== SAMMEL_STIL) setStil(gewaehlt.stil);
    else if (startStil) setStil(startStil);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wert, startStil]);

  const q = glatt(suche);
  const passt = (e) => !q || glatt(e.name).includes(q) || (e.alt_namen || []).some((a) => glatt(a).includes(q));
  const formen = katalog.filter((e) => e.stil !== SAMMEL_STIL && (stil === ALLE || e.stil === stil) && passt(e));
  const sammel = katalog.filter((e) => e.stil === SAMMEL_STIL && passt(e));
  const zeigeStil = stil === ALLE;

  const zeile = (e) => (
    <button
      type="button"
      key={e.id}
      className={`fwa-form ${e.id === wert ? 'fwa-an' : ''}`}
      onClick={() => onWaehle(e.id === wert ? null : e)}
      disabled={beschaeftigt}
      title={(e.alt_namen || []).length ? `Auch: ${e.alt_namen.join(', ')}` : e.name}
    >
      <span className="fwa-name">{e.name}</span>
      {zeigeStil && e.stil !== SAMMEL_STIL && <span className="fwa-stil">{e.stil}</span>}
      {(e.alt_namen || []).length > 0 && <span className="fwa-alt">{e.alt_namen.slice(0, 2).join(' · ')}</span>}
    </button>
  );

  return (
    <div className={`fwa ${kompakt ? 'fwa-kompakt' : ''}`}>
      <div className="fwa-kopf">
        <label className="fwa-feld">
          <span>Stil</span>
          <select value={stil} onChange={(ev) => setStil(ev.target.value)} disabled={beschaeftigt}>
            <option value={ALLE}>Alle Stile</option>
            {stile.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <label className="fwa-feld fwa-such">
          <span>Suchen</span>
          <input
            type="search"
            value={suche}
            onChange={(ev) => setSuche(ev.target.value)}
            placeholder="Name filtern, z. B. Heian"
            autoComplete="off"
          />
        </label>
      </div>
      <div className="fwa-liste" role="listbox" aria-label="Formen">
        {formen.map(zeile)}
        {formen.length === 0 && <div className="fwa-leer">Keine Form passt zu „{suche}“ in diesem Stil.</div>}
        {sammel.length > 0 && <div className="fwa-trenner">Allgemein</div>}
        {sammel.map(zeile)}
      </div>
      <div className="fwa-fuss">
        <span>{gewaehlt ? <>Gewählt: <b>{gewaehlt.name}</b>{gewaehlt.stil !== SAMMEL_STIL ? ` (${gewaehlt.stil})` : ''}</> : 'Noch keine Form gewählt'}</span>
        {gewaehlt && (
          <button type="button" className="fwa-weg" onClick={() => onWaehle(null)} disabled={beschaeftigt}>Form entfernen</button>
        )}
      </div>
    </div>
  );
}
